export interface ProcessingResult {
  blobs: Blob[];
  type: 'ok' | 'unsupported';
}

/* ========== dataURL 직접 생성 함수 (Blob 중간 단계 없음) ========== */

/** Blob → WebP 우선 (최대 1000px, 80%) / 실패 시 JPEG 85% fallback → dataURL */
function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const max = 1000;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);

      // Try WebP first (30-40% smaller than JPEG at same quality)
      let dataUrl: string | null = null;
      try {
        dataUrl = c.toDataURL('image/webp', 0.80);
      } catch {
        dataUrl = null;
      }
      // Fallback to JPEG if WebP fails or returns empty
      if (!dataUrl || dataUrl.length < 100) {
        dataUrl = c.toDataURL('image/jpeg', 0.85);
      }
      resolve(dataUrl);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

/** FileList → dataURL 배열 (이미지/PDF/ZIP) */
export async function processFilesToDataUrls(files: FileList): Promise<string[]> {
  const readFile = (file: File): Promise<ArrayBuffer> =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = (e) => resolve(e.target?.result as ArrayBuffer);
      r.onerror = reject;
      r.readAsArrayBuffer(file);
    });

  const results: string[] = [];

  for (const file of Array.from(files)) {
    // 이미지
    if (file.type.startsWith('image/')) {
      const dataUrl = await blobToDataUrl(file);
      if (dataUrl) results.push(dataUrl);
      continue;
    }

    // PDF
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
      const buf = await readFile(file);
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const vp = page.getViewport({ scale: 2.0 });
        const s = vp.width > 1200 ? 1200 / vp.width : 1;
        const f = page.getViewport({ scale: 2.0 * s });
        const c = document.createElement('canvas');
        c.width = Math.round(f.width);
        c.height = Math.round(f.height);
        await page.render({ canvasContext: c.getContext('2d')!, viewport: f }).promise;
        const dataUrl = c.toDataURL('image/jpeg', 0.85);
        if (dataUrl.startsWith('data:image')) results.push(dataUrl);
      }
      continue;
    }

    // ZIP
    if (file.type.includes('zip') || file.name.toLowerCase().endsWith('.zip')) {
      const JSZip = await import('jszip').then((m) => m.default || m);
      const buf = await readFile(file);
      const z = await JSZip.loadAsync(buf);
      const items = Object.values(z.files)
        .filter((e: any) => !e.dir && /\.(jpe?g|png|gif|webp)$/i.test(e.name))
        .sort((a: any, b: any) => a.name.localeCompare(b.name));

      for (const item of items) {
        const raw = await (item as any).async('blob');
        const dataUrl = await blobToDataUrl(raw);
        if (dataUrl) results.push(dataUrl);
      }
      continue;
    }
  }

  return results;
}

/* ========== Image upload helpers (local preview only) ========== */

function getFileExtension(filename: string) {
  const parts = filename.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() || '' : '';
}

export function isImageFile(file: File) {
  const ext = getFileExtension(file.name);
  const imageExts = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif'];
  const mime = (file.type || '').toLowerCase();

  return mime.startsWith('image/') || imageExts.includes(ext);
}

export function isPdfFile(file: File) {
  const ext = getFileExtension(file.name);
  const mime = (file.type || '').toLowerCase();
  return mime === 'application/pdf' || ext === 'pdf';
}

export function isZipFile(file: File) {
  const ext = getFileExtension(file.name);
  const mime = (file.type || '').toLowerCase();

  return (
    mime === 'application/zip' ||
    mime === 'application/x-zip-compressed' ||
    ext === 'zip'
  );
}

export async function optimizeImage(file: File | Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file);

  const maxWidth = 1200;
  const scale = Math.min(1, maxWidth / bitmap.width);

  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context를 만들 수 없습니다.');

  ctx.drawImage(bitmap, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.8);
  });

  if (!blob) throw new Error('이미지 압축에 실패했습니다.');

  return blob;
}

/* ========== PDF/ZIP → 슬라이드 ========== */

async function processPdfToSlides(file: File) {
  const pdfjsLib = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  const slides: { id: string; file: Blob; previewUrl: string; name: string }[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const vp = page.getViewport({ scale: 2.0 });
    const s = vp.width > 1200 ? 1200 / vp.width : 1;
    const f = page.getViewport({ scale: 2.0 * s });
    const c = document.createElement('canvas');
    c.width = Math.round(f.width);
    c.height = Math.round(f.height);
    await page.render({ canvasContext: c.getContext('2d')!, viewport: f }).promise;

    const blob = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/jpeg', 0.8));
    if (!blob) continue;

    const previewUrl = URL.createObjectURL(blob);
    slides.push({
      id: crypto.randomUUID(),
      file: blob,
      previewUrl,
      name: `${file.name} (p.${i})`,
    });
  }

  return slides;
}

async function processZipToSlides(file: File) {
  const JSZip = await import('jszip').then((m) => m.default || m);
  const buf = await file.arrayBuffer();
  const z = await JSZip.loadAsync(buf);
  const items = Object.values(z.files)
    .filter((e: any) => !e.dir && /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(e.name))
    .sort((a: any, b: any) => a.name.localeCompare(b.name));

  const slides: { id: string; file: Blob; previewUrl: string; name: string }[] = [];

  for (const item of items) {
    const raw = await (item as any).async('blob');
    const optimized = await optimizeImage(raw);
    const previewUrl = URL.createObjectURL(optimized);
    slides.push({
      id: crypto.randomUUID(),
      file: optimized,
      previewUrl,
      name: item.name,
    });
  }

  return slides;
}

export async function processFilesToOptimizedBlobs(files: File[]) {
  const results: { id: string; file: Blob; previewUrl: string; name: string }[] = [];

  for (const file of files) {
    if (isImageFile(file)) {
      const optimized = await optimizeImage(file);
      const previewUrl = URL.createObjectURL(optimized);

      results.push({
        id: crypto.randomUUID(),
        file: optimized,
        previewUrl,
        name: file.name,
      });
    } else if (isPdfFile(file)) {
      const pdfSlides = await processPdfToSlides(file);
      results.push(...pdfSlides);
    } else if (isZipFile(file)) {
      const zipSlides = await processZipToSlides(file);
      results.push(...zipSlides);
    } else {
      throw new Error(`[${file.name}] 이미지, PDF, ZIP 파일만 지원합니다.`);
    }
  }

  return results;
}

/* ========== 기존 Blob 기반 함수 (하위 호환) ========== */

const readFile = (file: File): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target?.result as ArrayBuffer);
    r.onerror = reject;
    r.readAsArrayBuffer(file);
  });

/** Blob → 600px JPEG 60% */
async function resize(blob: Blob): Promise<Blob | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const max = 600;
      const scale = img.width > max ? max / img.width : 1;
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
      c.toBlob(resolve, 'image/jpeg', 0.6);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => {
      if (typeof r.result === 'string') resolve(r.result);
      else reject(new Error('base64 변환 실패'));
    };
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/** 단일 파일 처리 */
export async function processFile(file: File): Promise<ProcessingResult> {
  // 이미지
  if (file.type.startsWith('image/')) {
    const b = await resize(file);
    if (b) return { blobs: [b], type: 'ok' };
    return { blobs: [file], type: 'ok' };
  }

  // PDF
  if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
    const pdfjsLib = await import('pdfjs-dist');
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

    const buf = await readFile(file);
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    const out: Blob[] = [];

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const vp = page.getViewport({ scale: 0.5 });
      const s = vp.width > 600 ? 600 / vp.width : 1;
      const f = page.getViewport({ scale: 0.5 * s });
      const c = document.createElement('canvas');
      c.width = Math.round(f.width);
      c.height = Math.round(f.height);
      await page.render({ canvasContext: c.getContext('2d')!, viewport: f }).promise;
      const b = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/jpeg', 0.6));
      if (b) out.push(b);
    }
    return { blobs: out, type: 'ok' };
  }

  // ZIP
  if (file.type.includes('zip') || file.name.toLowerCase().endsWith('.zip')) {
    const JSZip = await import('jszip').then((m) => m.default || m);
    const buf = await readFile(file);
    const z = await JSZip.loadAsync(buf);
    const items = Object.values(z.files)
      .filter((e: any) => !e.dir && /\.(jpe?g|png|gif|webp)$/i.test(e.name))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));

    const out: Blob[] = [];
    for (const item of items) {
      const raw = await (item as any).async('blob');
      const b = await resize(raw);
      out.push(b || raw);
    }
    return { blobs: out, type: 'ok' };
  }

  return { blobs: [], type: 'unsupported' };
}

/** 파일리스트 → 전부 처리 → Blob[] */
export async function processFiles(files: FileList): Promise<Blob[]> {
  const list: Blob[] = [];
  for (const f of Array.from(files)) {
    const r = await processFile(f);
    if (r.type === 'ok') list.push(...r.blobs);
  }
  return list;
}